import { createTheme } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// SVA Volunteer Webapp — 2026 redesign design tokens
// Source of truth: Figma "SVA - Product" file C34dfadGCkhfoCbrDeEBDH,
// page "Volunteer Flow - Updated" (796:30954) + Component page (7:422).
// System = Untitled UI grey/semantic scale with SVA orange as primary.
// Fonts: Söhne (display) + Inter (body/UI). Söhne is licensed, so we ship
// an Inter-first stack that upgrades to Söhne automatically if it is installed.
// This theme is applied via a scoped <ThemeProvider> on redesigned screens only,
// so it does not disturb the legacy Raleway/navy theme until each screen migrates.
// ---------------------------------------------------------------------------

export const BRAND = {
  orange: "#FE5100", // primary brand orange — rgba(254,81,0,1), from Figma header block
  orangeDark: "#D64500",
  orangeTint: "#FFF1EB",
  orangePattern: "#FF7A33", // lighter orange used only in the woven-triangle header texture
};

// Untitled UI grey scale (matches the hexes pulled from the Figma screens)
export const GREY = {
  25: "#FCFCFD",
  50: "#F9FAFB",
  100: "#F2F4F7",
  200: "#EAECF0",
  300: "#D0D5DD",
  400: "#98A2B3",
  500: "#667085",
  600: "#475467",
  700: "#344054",
  800: "#1D2939",
  900: "#0C111D",
};

export const SEMANTIC = {
  success: "#17B26A",
  successBg: "#E5EEEB",
  warning: "#F79009",
  warningBg: "#FEF0C7",
  info: "#2E90FA",
  infoBg: "#EFF8FF",
};

const FONT_DISPLAY = '"Söhne", "Inter", system-ui, -apple-system, sans-serif';
const FONT_BODY = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';

export const REDESIGN_THEME = createTheme({
  shape: { borderRadius: 16 },
  palette: {
    mode: "light",
    primary: { main: BRAND.orange, dark: BRAND.orangeDark, light: BRAND.orangeTint, contrastText: "#FFFFFF" },
    success: { main: SEMANTIC.success, light: SEMANTIC.successBg },
    warning: { main: SEMANTIC.warning, light: SEMANTIC.warningBg },
    info: { main: SEMANTIC.info, light: SEMANTIC.infoBg },
    grey: GREY,
    background: { default: GREY[200], paper: "#FFFFFF" }, // page bg rgba(234,236,240,1) = #EAECF0
    text: { primary: GREY[900], secondary: GREY[600], disabled: GREY[400] },
    divider: GREY[200],
    // legacy keys kept so shared components don't crash if reused
    green: { main: SEMANTIC.success },
    opposite: { main: GREY[50] },
  },
  typography: {
    fontFamily: FONT_BODY,
    h1: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 48, lineHeight: "60px", color: GREY[900] },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 36, lineHeight: "44px", color: GREY[900] },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 30, lineHeight: "38px", color: GREY[900] },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 24, lineHeight: "32px", color: GREY[900] },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 20, lineHeight: "30px", color: GREY[900] },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 18, lineHeight: "28px", color: GREY[900] },
    subtitle1: { fontWeight: 600, fontSize: 16, lineHeight: "24px" },
    subtitle2: { fontWeight: 500, fontSize: 14, lineHeight: "20px", color: GREY[600] },
    body1: { fontWeight: 400, fontSize: 16, lineHeight: "24px", color: GREY[600] },
    body2: { fontWeight: 400, fontSize: 14, lineHeight: "20px", color: GREY[600] },
    button: { textTransform: "none", fontWeight: 600, fontSize: 14 },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 20, border: `1px solid ${GREY[200]}`, boxShadow: "0 1px 2px rgba(16,24,40,0.05)" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, paddingInline: 16, paddingBlock: 10 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // The dropdown defaults to max-height 40vh, which on a phone is far too
        // tall — with the on-screen keyboard open it crowds the small area above
        // it and is awkward to scroll. Cap it to a compact box on mobile so the
        // list sits neatly above the keyboard and scrolls internally; keep the
        // roomier 40vh on tablet/desktop where there's space.
        listbox: ({ theme }) => ({
          maxHeight: 240,
          [theme.breakpoints.up("sm")]: { maxHeight: "40vh" },
        }),
      },
    },
  },
});

export default REDESIGN_THEME;
